import { useState } from "react";

import { IconPlus, IconSliders, IconX } from "@/components/Icons";

import { Button, Input, Select } from "./controls";
import { MenuItem, MenuLabel, Popover } from "./Popover";
import styles from "./kit.module.css";

/**
 * Filtering a list whose columns nobody knew in advance.
 *
 * A sport invents its own details, so we can never lay out a filter panel with
 * the right controls on it. What we *can* know is that every filter is the same
 * three parts — a field, a test, and a value — so that is what gets designed,
 * and the sport's own declaration supplies the rest.
 *
 * Applied filters are chips rather than a hidden panel. A panel that folds away
 * lets someone stare at 12 rows of 500 and wonder where the other 488 went; a
 * chip is the answer, in the open, one click from being removed.
 */

export type FilterKind = "text" | "number" | "date" | "choice";

export type FilterField = {
  key: string;
  label: string;
  kind: FilterKind;
  /** For `choice` — the values that sport declared. */
  options?: string[];
};

export type AppliedFilter = {
  key: string;
  test: string;
  value: string;
};

/** The tests that make sense for each kind of detail. Wording is the
 * operator's, not a query language's. */
const TESTS: Record<FilterKind, string[]> = {
  text: ["contains", "is", "is not", "is empty"],
  number: ["is", "at least", "at most"],
  date: ["on", "after", "before"],
  choice: ["is", "is not"],
};

/** The word shown on a chip when no value has been typed yet. */
const BLANK = "…";

export function Filters({
  fields,
  value,
  onChange,
}: {
  fields: FilterField[];
  value: AppliedFilter[];
  onChange: (next: AppliedFilter[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const fieldFor = (key: string) => fields.find((f) => f.key === key);
  const unused = fields.filter((f) => !value.some((v) => v.key === f.key));

  function add(field: FilterField) {
    setAdding(false);
    onChange([...value, { key: field.key, test: TESTS[field.kind][0], value: "" }]);
    // Straight into editing the value: adding a filter and then having to click
    // it again to say what you meant is a wasted step every single time.
    setEditing(field.key);
  }

  function update(key: string, patch: Partial<AppliedFilter>) {
    onChange(value.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  }

  function remove(key: string) {
    onChange(value.filter((f) => f.key !== key));
  }

  return (
    <>
      {value.map((applied) => {
        const field = fieldFor(applied.key);
        if (!field) return null;
        const needsValue = applied.test !== "is empty";
        return (
          <Popover
            key={applied.key}
            open={editing === applied.key}
            onClose={() => setEditing(null)}
            wide
            trigger={
              <span className={styles.chip}>
                <button
                  type="button"
                  className={styles.chipMain}
                  onClick={() => setEditing(editing === applied.key ? null : applied.key)}
                >
                  <span className={styles.chipField}>{field.label}</span>
                  <span className={styles.chipTest}>{applied.test}</span>
                  {needsValue && (
                    <span className={styles.chipValue}>{applied.value || BLANK}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.chipX}
                  aria-label={`Remove the ${field.label} filter`}
                  onClick={() => remove(applied.key)}
                >
                  <IconX size={13} />
                </button>
              </span>
            }
          >
            <div style={{ display: "grid", gap: "var(--s2)" }}>
              <Select
                aria-label="Test"
                value={applied.test}
                onChange={(e) => update(applied.key, { test: e.target.value })}
              >
                {TESTS[field.kind].map((test) => (
                  <option key={test} value={test}>
                    {test}
                  </option>
                ))}
              </Select>

              {needsValue &&
                (field.kind === "choice" ? (
                  <Select
                    aria-label="Value"
                    value={applied.value}
                    onChange={(e) => update(applied.key, { value: e.target.value })}
                  >
                    <option value="">Pick one</option>
                    {(field.options ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    aria-label="Value"
                    autoFocus
                    type={field.kind === "date" ? "date" : field.kind === "number" ? "number" : "text"}
                    value={applied.value}
                    placeholder={field.label}
                    onChange={(e) => update(applied.key, { value: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setEditing(null);
                    }}
                  />
                ))}
            </div>
          </Popover>
        );
      })}

      <Popover
        open={adding}
        onClose={() => setAdding(false)}
        trigger={
          <Button tone="subtle" onClick={() => setAdding(!adding)}>
            {value.length === 0 ? <IconSliders size={15} /> : <IconPlus size={15} />}
            {value.length === 0 ? "Filter" : "Add"}
          </Button>
        }
      >
        <MenuLabel>Filter by</MenuLabel>
        {unused.length === 0 ? (
          <MenuItem label="Everything is already filtered" onClick={() => setAdding(false)} disabled />
        ) : (
          unused.map((field) => (
            <MenuItem
              key={field.key}
              label={field.label}
              kind={field.kind}
              onClick={() => add(field)}
            />
          ))
        )}
      </Popover>

      {value.length > 0 && (
        <Button tone="subtle" onClick={() => onChange([])}>
          Clear
        </Button>
      )}
    </>
  );
}
